// Per-model fal video argument specs for the app.nz gateway. Declares which
// optional args each video model exposes in the UI + their ranges/options.
// Advanced args are opt-in: only sent when the user provides a value, so default
// behaviour is unchanged. Mirrors the openpaths spec.

export type NumRange = { min: number; max: number; step?: number; placeholder?: string };

export type VideoParamSpec = {
  resolutions: string[];
  durations: string[];
  aspectRatios: string[];
  generateAudio: boolean;
  enableSafetyChecker: boolean;
  imageToVideo: boolean;
  endImage: boolean;
  referenceToVideo: boolean;
  negativePrompt: boolean;
  seed: boolean;
  numFrames?: NumRange;
  framesPerSecond?: { options: number[] };
  guidanceScale?: NumRange;
  numInferenceSteps?: NumRange;
  outputFormats?: string[];
};

export const VIDEO_RESOLUTIONS = ['480p', '720p', '1080p'];
export const VIDEO_DURATIONS = ['auto', '4', '5', '6', '7', '8', '9', '10', '11', '12', '13', '14', '15'];
export const VIDEO_ASPECT_RATIOS = ['auto', '21:9', '16:9', '4:3', '1:1', '3:4', '9:16'];
export const VIDEO_OUTPUT_FORMATS = ['mp4', 'webm'];

const BASE: VideoParamSpec = {
  resolutions: VIDEO_RESOLUTIONS,
  durations: VIDEO_DURATIONS,
  aspectRatios: VIDEO_ASPECT_RATIOS,
  generateAudio: true,
  enableSafetyChecker: false,
  imageToVideo: false,
  endImage: false,
  referenceToVideo: false,
  negativePrompt: true,
  seed: true,
  outputFormats: VIDEO_OUTPUT_FORMATS,
};

const FAMILIES: Array<{ test: RegExp; spec: Partial<VideoParamSpec> }> = [
  {
    test: /h3-max\/relight|h3-max-relight/i,
    spec: {
      resolutions: ['480P', '768P', '1080P', '2K'],
      durations: ['3', '4', '5', '6', '7', '8', '9', '10', '11', '12', '13', '14', '15'],
      aspectRatios: ['adaptive', '16:9', '9:16', '1:1', '4:3', '3:4', '21:9'],
      generateAudio: false,
      negativePrompt: false,
      enableSafetyChecker: true,
      outputFormats: undefined,
    },
  },
  {
    test: /gemini-omni/i,
    spec: {
      resolutions: ['720p'],
      durations: ['3', '4', '5', '6', '7', '8', '9', '10'],
      aspectRatios: ['16:9'],
      generateAudio: false,
      negativePrompt: false,
      seed: false,
      outputFormats: ['mp4'],
    },
  },
  {
    test: /ltx/i,
    spec: {
      numFrames: { min: 9, max: 161, step: 8, placeholder: '121' },
      framesPerSecond: { options: [24, 25, 30] },
      guidanceScale: { min: 1, max: 10, step: 0.5, placeholder: '3' },
      numInferenceSteps: { min: 10, max: 50, placeholder: '30' },
    },
  },
  {
    test: /wan\//i,
    spec: {
      guidanceScale: { min: 1, max: 10, step: 0.5, placeholder: '5' },
      numInferenceSteps: { min: 10, max: 50, placeholder: '30' },
    },
  },
  {
    test: /hunyuan/i,
    spec: {
      guidanceScale: { min: 1, max: 10, step: 0.5, placeholder: '6' },
      numInferenceSteps: { min: 10, max: 50, placeholder: '30' },
    },
  },
  {
    test: /cogvideo/i,
    spec: {
      guidanceScale: { min: 1, max: 10, step: 0.5, placeholder: '7' },
      numInferenceSteps: { min: 10, max: 50, placeholder: '50' },
    },
  },
];

function detectInputs(modelId: string) {
  const isImageToVideo = /image-to-video|i2v/i.test(modelId);
  const isReference = /reference-to-video|reference/i.test(modelId);
  return { imageToVideo: isImageToVideo, endImage: isImageToVideo, referenceToVideo: isReference };
}

export function getVideoParamSpec(modelId: string): VideoParamSpec {
  const family = FAMILIES.find((f) => f.test.test(modelId));
  const isHappyHorse = modelId === 'alibaba/happy-horse/image-to-video';
  return {
    ...BASE,
    ...detectInputs(modelId),
    enableSafetyChecker: isHappyHorse,
    generateAudio: !isHappyHorse,
    ...(family?.spec ?? {}),
  };
}

export type VideoAdvancedValues = {
  negativePrompt: string;
  seed: string;
  numFrames: string;
  framesPerSecond: string;
  guidanceScale: string;
  numInferenceSteps: string;
  outputFormat: string;
};

export const EMPTY_ADVANCED: VideoAdvancedValues = {
  negativePrompt: '',
  seed: '',
  numFrames: '',
  framesPerSecond: '',
  guidanceScale: '',
  numInferenceSteps: '',
  outputFormat: '',
};
